import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  PoBreadcrumb,
  PoDialogService,
  PoModalAction,
  PoModalComponent,
  PoNotificationService,
  PoTableColumn,
} from '@po-ui/ng-components';
import { InventarioItem } from '../interface/inventario';
import { InventarioService } from '../service/inventario.service';

@Component({
  selector: 'app-inventariar',
  templateUrl: './inventariar.component.html',
  styleUrls: ['./inventariar.component.css'],
})
export class InventariarComponent implements OnInit {

  codigoInventario: string = '';
  area: string = '';
  lote: string = '';
  itens: Array<InventarioItem> = [];
  nLotesBipados: number = 0;
  qtdBipadaConfirmacao: number | null = null;
  lotesPlanejados: Set<string> = new Set();
  lotesBipadosNestaArea: Array<InventarioItem> = [];

  constructor(
    private inventarioService: InventarioService,
    private route: ActivatedRoute,
    private router: Router,
    private poDialogService: PoDialogService,
    private poNotificationService: PoNotificationService,
  ) {}

  @ViewChild('areaModal', { static: true }) areaModal!: PoModalComponent;
  @ViewChild('fecharAreaModal', { static: true }) fecharAreaModal!: PoModalComponent;

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.codigoInventario = params['CZ08COD'];
      this.carregarItens();
      this.areaModal.open();
    });
  }

  carregarItens(): void {
    this.inventarioService.getInventario(this.codigoInventario).subscribe((inv) => {
      this.itens = this.numerarItens(inv.items);
      this.lotesPlanejados = new Set(inv.items.map((i) => i.CZ08LOTE));
      this.nLotesBipados = this.lotesBipadosNestaArea.length;
    });
  }

  private numerarItens(itens: Array<InventarioItem>): Array<InventarioItem> {
    return itens.map((item, index) => ({ ...item, contador: index + 1 }));
  }

  confirmarArea: PoModalAction = {
    label: 'Confirmar',
    action: () => {
      const areaInformada = this.area.trim();
      if (!areaInformada) {
        this.poNotificationService.warning('Informe a área antes de continuar.');
        return;
      }

      this.inventarioService.getInventario(this.codigoInventario).subscribe({
        next: (inventario) => {
          this.itens = this.numerarItens(inventario.items);
          this.lotesPlanejados = new Set(inventario.items.map((item) => item.CZ08LOTE));

          const areaNormalizada = areaInformada.toLocaleUpperCase();
          const areaJaExiste = inventario.items.some(
            (item) => item.CZ08AREA?.trim().toLocaleUpperCase() === areaNormalizada,
          );

          if (areaJaExiste) {
            this.poNotificationService.warning('Essa área já existe neste inventário.');
            return;
          }

          this.area = areaInformada;
          this.lotesBipadosNestaArea = [];
          this.nLotesBipados = this.lotesBipadosNestaArea.length;
          this.areaModal.close();
        },
        error: () => {
          this.poNotificationService.error('Não foi possível validar a área no inventário.');
        },
      });
    },
  };

  get breadcrumb(): PoBreadcrumb {
    return {
      items: [
        { label: 'Inventariar', action: () => this.router.navigateByUrl('/estoque/inventario/inventariar') },
        { label: `Inventariar - ${this.codigoInventario}` },
      ],
    };
  }

  public readonly colunas: Array<PoTableColumn> = [
    { property: 'contador', label: 'Nº', width: '5%', type: 'number' },
    { property: 'CZ08LOTE', label: 'Lote', width: '15%' },
    { property: 'CB1COD', label: 'Cód. Produto', width: '12%' },
    { property: 'CB1DESC', label: 'Desc. Produto', width: '25%' },
    { property: 'NB8QTDORI', label: 'Qtd Peças', width: '10%', type: 'number' },
    { property: 'NB8VOLUME', label: 'Volume', width: '10%', type: 'number' },
    { property: 'CZ08AREA', label: 'Área', width: '10%' },
    { property: 'CUSRNOME', label: 'Usuário', width: '10%' },
    {
      property: 'CZ08ESTOQU', label: 'Estoque', width: '10%', type: 'label',
      labels: [
        { value: 'S', color: 'color-10', label: 'Sim' },
        { value: 'C', color: 'color-08', label: 'Conferir' },
        { value: '',  color: 'caption-tag-32', label: 'Não Lido' },
      ],
    },
  ];

  bipar(): void {
    if (!this.lote.trim()) { return; }

   let loteAtual = this.lote.trim();
    
    // Remove os três zeros iniciais quando o lote tiver mais de 7 caracteres
    if (loteAtual.length > 7 && loteAtual.substring(0, 3) === '000') {
    loteAtual = loteAtual.substring(3, loteAtual.length);
  }


    this.lote = '';

    const areasAnteriores = Array.from(new Set(
      this.itens
        .filter((item) => item.CZ08LOTE === loteAtual && item.CZ08ESTOQU?.trim())
        .map((item) => item.CZ08AREA?.trim() || '')
        .filter((areaAnterior) => areaAnterior.length > 0),
    ));

    if (areasAnteriores.length > 0) {
      const descricaoAreas = areasAnteriores.length === 1
        ? `na área ${areasAnteriores[0]}`
        : `nas áreas ${areasAnteriores.join(', ')}`;
      this.poDialogService.confirm({
        title: 'Lote já bipado',
        message: `Lote ${loteAtual} já foi bipado ${descricaoAreas}. Deseja bipar novamente?`,
        literals: { confirm: 'Sim', cancel: 'Não' },
        confirm: () => this.registrarBipagem(loteAtual),
      });
      return;
    }

    this.registrarBipagem(loteAtual);
  }

  private registrarBipagem(loteAtual: string): void {
    this.inventarioService.consultarLote(loteAtual).subscribe({
      next: (ret) => {
        const itemBipado: InventarioItem = {
          CZ08LOTE: loteAtual,
          CB1COD: ret.CB1COD,
          CB1DESC: ret.CB1DESC,
          NB8QTDORI: ret.NB8QTDORI,
          NB8VOLUME: ret.NB8VOLUME,
          CZ08AREA: this.area,
          CZ08USER: ret.CZ08USER,
          CUSRNOME: ret.CUSRNOME,
          CZ08ESTOQU: this.lotesPlanejados.has(loteAtual) ? 'S' : 'C',
        };

        this.lotesBipadosNestaArea = [...this.lotesBipadosNestaArea, itemBipado];

        const indiceNaoLido = this.itens.findIndex(
          (item) => item.CZ08LOTE === loteAtual && !item.CZ08ESTOQU?.trim(),
        );

        if (indiceNaoLido >= 0) {
          this.itens[indiceNaoLido] = itemBipado;
          this.itens = this.numerarItens(this.itens);
        } else {
          this.itens = this.numerarItens([...this.itens, itemBipado]);
        }

        this.nLotesBipados = this.lotesBipadosNestaArea.length;
      },
      error: () => {
        this.poNotificationService.error(`Não foi possível consultar o lote ${loteAtual}.`);
      },
    });
  }

  abrirFecharArea(): void {
    this.qtdBipadaConfirmacao = null;
    this.fecharAreaModal.open();
  }

  fecharArea: PoModalAction = {
    label: 'Confirmar Fechamento',
    action: () => {
      if (this.qtdBipadaConfirmacao !== this.nLotesBipados) {
        this.poNotificationService.error('A quantidade informada está incorreta.');
        return;
      }

      const payload = {
        items: this.lotesBipadosNestaArea.map((item) => ({
          CZ08LOTE: item.CZ08LOTE,
          CZ08AREA: item.CZ08AREA || '',
          CZ08USER: item.CZ08USER || '',
          CZ08ESTOQU: item.CZ08ESTOQU || '',
        })),
      };

      this.inventarioService.fecharArea(this.codigoInventario, payload).subscribe(() => {
        this.poNotificationService.success('Área gravada com sucesso!');
        this.fecharAreaModal.close();
        this.router.navigateByUrl('/estoque/inventario/inventariar');
      });
    },
  };

  cancelarFechar: PoModalAction = {
    label: 'Cancelar',
    danger: true,
    action: () => this.fecharAreaModal.close(),
  };
}
